import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { pb, errorMessage } from '../pb'

/**
 * Sign in, or create a Torahly account.
 *
 * `identity` takes an email **or** a username, because the users collection lists both in
 * passwordAuth.identityFields. Nothing here has to tell them apart, so nothing here tries.
 *
 * The masthead is above this even when signed out, so the first thing a visitor meets is the
 * date rather than a login box. Deliberate: the calendar is public, and it says what this
 * place is faster than any tagline.
 */
export function LoginForm() {
  // /register opens on the create-account form; every other path opens on sign in.
  const [registering, setRegistering] = useState(useLocation().pathname === '/register')
  const [identity, setIdentity] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (registering) {
        // Creating the record does not return a token, so registration signs in straight
        // after, the same two-step the app and Torahly both use.
        await pb.collection('users').create({
          email: identity,
          password,
          passwordConfirm: password,
          name: displayName,
          role: 'member',
        })
      }
      await pb.collection('users').authWithPassword(identity, password)
    } catch (err) {
      setError(errorMessage(err, registering ? 'Could not create your account' : 'Could not sign in'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="stack">
      <div className="card">
        <h2 className="panel-title">{registering ? 'Create your account' : 'Sign in'}</h2>
        <p className="panel-note">The same account you use in Covellah and Torahly.</p>

        <form className="stack" onSubmit={submit}>
          {registering && (
            <div>
              <label className="field-label" htmlFor="name">
                Your name
              </label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </div>
          )}

          <div>
            <label className="field-label" htmlFor="identity">
              {registering ? 'Email' : 'Email or username'}
            </label>
            <input
              id="identity"
              type={registering ? 'email' : 'text'}
              autoComplete={registering ? 'email' : 'username'}
              value={identity}
              onChange={(e) => setIdentity(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="field-label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete={registering ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && <p className="notice">{error}</p>}

          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? 'One moment' : registering ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <button
          className="btn-quiet"
          type="button"
          onClick={() => {
            setRegistering(!registering)
            setError(null)
          }}
        >
          {registering ? 'I already have an account' : 'I need an account'}
        </button>
      </div>
    </section>
  )
}
